const SITE_ORIGIN = "https://www.chinaone.co.kr";
const SITE_NAMES = {
  ko: "차이나원",
  zh: "chinaone",
} as const;
const OG_IMAGE_URL = `${SITE_ORIGIN}/og-image.png`;
const PAGE_MANAGED_DOCUMENT_TITLES = new Set([
  "/stock-detail-preview",
  "/stock-in-out-details",
  "/stock-sku-composition",
]);

const siteKeywords = {
  ko: "중국 구매대행, 중국 배송대행, 중국 배대지, 1688 구매대행, 타오바오 구매대행, 중국 직구, 한중 물류, 중국 3PL, 한국 3PL, LCL, FCL, 해운 물류, 쿠팡 로켓그로스, 상품 검수, 합배송",
  zh: "中国采购代理, 中国转运, 韩国转运, 1688采购, 淘宝采购, 中韩物流, 中国3PL, 韩国3PL, LCL, FCL, 海运物流, Coupang入仓, 商品验货, 合箱发货",
};

type PageCopy = { title: string; description: string };
type LocalizedCopy = { ko: PageCopy; zh: PageCopy };

export function formatDocumentTitle(pageTitle: string, locale: string) {
  const language = locale.toLowerCase().startsWith("zh") ? "zh" : "ko";
  const siteName = SITE_NAMES[language];
  const normalizedTitle = String(pageTitle || "")
    .replace(/^(?:차이나원|chinaone)\s*[|｜·—–-]\s*/i, "")
    .replace(/\s*-\s*(?:차이나원|chinaone)$/i, "")
    .trim();

  return normalizedTitle ? `${normalizedTitle} - ${siteName}` : siteName;
}

declare global {
  interface Window {
    ChinaOnePageTitles?: {
      resolve: (location: string, locale: string) => string;
    };
  }
}

const pageCopy: Record<string, LocalizedCopy> = {
  "/": {
    ko: {
      title: "중국 구매대행·배송대행·3PL·LCL/FCL 한중 물류 - 차이나원",
      description: "차이나원은 중국 구매대행·배송대행부터 1688·타오바오 구매, 중국·한국 3PL, LCL/FCL 해운, 쿠팡 로켓그로스 입고까지 지원하는 한중 물류 플랫폼입니다.",
    },
    zh: {
      title: "中韩采购、转运、3PL与LCL/FCL物流 - chinaone",
      description: "ChinaOne 提供1688及淘宝采购代理、中韩转运、中国与韩国3PL仓储、LCL/FCL海运和Coupang入仓服务。",
    },
  },
  "/guide": {
    ko: {
      title: "이용안내 - 차이나원",
      description: "차이나원 서비스 이용 방법과 업무 안내를 확인하세요.",
    },
    zh: {
      title: "使用指南 - chinaone",
      description: "查看 Chinaone 的服务说明和使用指南。",
    },
  },
  "/notice-center": {
    ko: {
      title: "공지사항 - 차이나원",
      description: "차이나원의 공지사항과 서비스 소식을 확인하세요.",
    },
    zh: {
      title: "公告中心 - chinaone",
      description: "查看 Chinaone 的公告和服务消息。",
    },
  },
  "/policy/terms": {
    ko: { title: "이용약관 - 차이나원", description: "차이나원의 서비스 이용약관을 확인하세요." },
    zh: { title: "服务条款 - chinaone", description: "查看 Chinaone 的服务条款。" },
  },
  "/policy/privacy": {
    ko: { title: "개인정보처리방침 - 차이나원", description: "차이나원의 개인정보처리방침을 확인하세요." },
    zh: { title: "隐私政策 - chinaone", description: "查看 Chinaone 的隐私政策。" },
  },
};

const boardCopy: Record<string, LocalizedCopy> = {
  "26": {
    ko: { title: "공지사항 목록 - 차이나원", description: "차이나원의 공지사항과 최신 서비스 소식을 확인하세요." },
    zh: { title: "公告列表 - chinaone", description: "查看 Chinaone 的最新公告和服务消息。" },
  },
  "27": {
    ko: { title: "3PL 공지사항 - 차이나원", description: "차이나원의 3PL 서비스 공지와 물류센터 소식을 확인하세요." },
    zh: { title: "3PL公告 - chinaone", description: "查看 Chinaone 的3PL服务公告和仓储动态。" },
  },
  "28": {
    ko: { title: "자주 묻는 질문 - 차이나원", description: "차이나원 서비스에 관한 자주 묻는 질문을 확인하세요." },
    zh: { title: "常见问题 - chinaone", description: "查看 Chinaone 服务的常见问题。" },
  },
  "29": {
    ko: { title: "이벤트 - 차이나원", description: "차이나원의 최신 이벤트와 혜택 소식을 확인하세요." },
    zh: { title: "活动中心 - chinaone", description: "查看 Chinaone 的最新活动和优惠信息。" },
  },
};

const detailCopy: Record<"guide" | "notice", LocalizedCopy> = {
  guide: {
    ko: { title: "이용안내 상세 - 차이나원", description: "차이나원 이용안내의 자세한 내용을 확인하세요." },
    zh: { title: "指南详情 - chinaone", description: "查看 Chinaone 使用指南的详细内容。" },
  },
  notice: {
    ko: { title: "공지사항 상세 - 차이나원", description: "차이나원 공지사항의 자세한 내용을 확인하세요." },
    zh: { title: "公告详情 - chinaone", description: "查看 Chinaone 公告的详细内容。" },
  },
};

function setMeta(attribute: "name" | "property", key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.content = content;
}

function setCanonical(path: string | null) {
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!path) {
    existing?.remove();
    return;
  }
  const element = existing || document.createElement("link");
  element.rel = "canonical";
  element.href = new URL(path, SITE_ORIGIN).href;
  if (!existing) document.head.appendChild(element);
}

export function applyRouteSeo(
  location: string,
  locale: string,
  article?: { title?: string; description?: string },
) {
  const routeUrl = new URL(location, SITE_ORIGIN);
  const path = routeUrl.pathname;
  const language = locale.toLowerCase().startsWith("zh") ? "zh" : "ko";
  const listCategory = ["/guide", "/notice-center", "/bbs/list"].includes(path)
    ? routeUrl.searchParams.get("categoryId")
    : null;
  const categoryCopy = path === "/notice-center" || path === "/bbs/list"
    ? (listCategory ? boardCopy[listCategory]?.[language] : null)
    : null;
  const detailKind = /^\/guide\/\d+$/.test(path)
    ? "guide"
    : /^\/bbs\/detail\/\d+$/.test(path) ? "notice" : null;
  const copy = categoryCopy
    || pageCopy[path]?.[language]
    || (detailKind ? detailCopy[detailKind][language] : null);
  const page = Number(routeUrl.searchParams.get("page") || "1");
  const filteredList = ["/guide", "/notice-center", "/bbs/list"].includes(path)
    && (page > 1 || Boolean(routeUrl.searchParams.get("search")));
  const hasValidListCategory = Boolean(
    listCategory && /^\d+$/.test(listCategory) && Number(listCategory) > 0,
  );
  const indexableList = path === "/guide"
    ? hasValidListCategory
    : path === "/notice-center"
      ? hasValidListCategory
      : path === "/bbs/list"
        ? false
        : null;
  const indexable = Boolean(copy)
    && !filteredList
    && (indexableList === null || indexableList);
  const canonicalPath = indexableList
    ? `${path}?categoryId=${listCategory}`
    : path;
  const routeTitle = window.ChinaOnePageTitles?.resolve(location, locale) || "";
  const title = article?.title
    ? formatDocumentTitle(article.title, locale)
    : copy?.title
      ? formatDocumentTitle(copy.title, locale)
      : routeTitle || SITE_NAMES[language];
  const description = article?.description || copy?.description || pageCopy["/"][language].description;
  const imageAlt = language === "zh"
    ? "ChinaOne 中韩采购与物流服务"
    : "차이나원 중국 구매대행·한중 물류 서비스";

  document.documentElement.lang = language === "zh" ? "zh-CN" : "ko-KR";
  if (!PAGE_MANAGED_DOCUMENT_TITLES.has(path)) document.title = title;
  setMeta("name", "description", description);
  setMeta("name", "keywords", siteKeywords[language]);
  setMeta("property", "og:type", detailKind ? "article" : "website");
  setMeta("property", "og:site_name", SITE_NAMES[language]);
  setMeta("property", "og:locale", language === "zh" ? "zh_CN" : "ko_KR");
  setMeta("property", "og:title", title);
  setMeta("property", "og:description", description);
  setMeta("property", "og:image", OG_IMAGE_URL);
  setMeta("property", "og:image:secure_url", OG_IMAGE_URL);
  setMeta("property", "og:image:type", "image/png");
  setMeta("property", "og:image:width", "1200");
  setMeta("property", "og:image:height", "630");
  setMeta("property", "og:image:alt", imageAlt);
  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", title);
  setMeta("name", "twitter:description", description);
  setMeta("name", "twitter:image", OG_IMAGE_URL);
  setMeta("name", "twitter:image:alt", imageAlt);

  const robots = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (indexable) {
    robots?.remove();
    setCanonical(canonicalPath);
    setMeta("property", "og:url", new URL(canonicalPath, SITE_ORIGIN).href);
  } else {
    setMeta("name", "robots", "noindex, follow");
    setCanonical(null);
    document.head.querySelector('meta[property="og:url"]')?.remove();
  }
}

export function articleDescription(content?: string) {
  return (content || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;|&#160;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 155);
}
